import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import {
  getAlertSessionAlerts,
  getAlertSessionEvent,
  getAlertSessionSummary,
  getAlertSessionTimeline,
  keepAlertSession,
  releaseAlertSession,
} from '@kirin/api-client/api/agent-sessions';
import {
  SESSION_ALERTS_PAGE_SIZE,
  SESSION_TIMELINE_PAGE_SIZE,
  type SessionSummary,
  type SessionTimelineDirection,
  type SessionTimelineResponse,
} from '@kirin/api-client/contracts/agent-sessions';
import { ApiError } from '@kirin/api-client/errors';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';

/**
 * Agent session timeline hooks (KIR-4445). Every call is addressed by the
 * alert occurrence; the server derives the session from it.
 * `organizationId` null → personal-account routes.
 */

/** About 500 rows in memory; dropped pages are refetched on scroll back. */
export const SESSION_TIMELINE_MAX_PAGES = 10;

export interface TimelinePageParam {
  cursor?: string;
  direction?: SessionTimelineDirection;
}

export function useAlertSessionSummary(
  organizationId: string | null,
  occurrenceId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.sessionTimeline.summary(
      organizationId,
      occurrenceId ?? ''
    ),
    queryFn: () =>
      getAlertSessionSummary(apiClient, organizationId, occurrenceId ?? ''),
    enabled: enabled && !!occurrenceId,
    staleTime: 30_000,
  });
}

/**
 * `viewId` names this opening of the timeline; it rides on the opening read
 * (and its refetches) so the server audits the view once, not once per poll.
 */
export function useAlertSessionTimeline(
  organizationId: string | null,
  occurrenceId: string,
  viewId: string,
  enabled = true
) {
  return useInfiniteQuery<
    SessionTimelineResponse,
    Error,
    InfiniteData<SessionTimelineResponse, TimelinePageParam>,
    ReturnType<typeof queryKeys.sessionTimeline.timeline>,
    TimelinePageParam
  >({
    queryKey: queryKeys.sessionTimeline.timeline(
      organizationId,
      occurrenceId,
      viewId
    ),
    queryFn: ({ pageParam }) =>
      getAlertSessionTimeline(apiClient, organizationId, occurrenceId, {
        ...pageParam,
        ...(pageParam.cursor
          ? { limit: SESSION_TIMELINE_PAGE_SIZE }
          : { viewId }),
      }),
    initialPageParam: {},
    getNextPageParam: lastPage =>
      lastPage.nextCursor
        ? { cursor: lastPage.nextCursor, direction: 'after' }
        : undefined,
    getPreviousPageParam: firstPage =>
      firstPage.prevCursor
        ? { cursor: firstPage.prevCursor, direction: 'before' }
        : undefined,
    maxPages: SESSION_TIMELINE_MAX_PAGES,
    enabled,
    refetchOnWindowFocus: false,
    // Keyed per opening, so a closed opening's pages are never reused.
    gcTime: 0,
  });
}

/** KIR-4785: every alert in the occurrence's session, one page at a time. */
export function useSessionAlerts(
  organizationId: string | null,
  occurrenceId: string,
  page: number,
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.sessionTimeline.alerts(
      organizationId,
      occurrenceId,
      page
    ),
    queryFn: () =>
      getAlertSessionAlerts(apiClient, organizationId, occurrenceId, {
        page,
        limit: SESSION_ALERTS_PAGE_SIZE,
      }),
    enabled,
    placeholderData: keepPreviousData,
    // A 404 is an answer (no session, or not the caller's), not a blip.
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status === 404) && failureCount < 3,
  });
}

export function useAlertSessionEvent(
  organizationId: string | null,
  occurrenceId: string,
  eventId: string | null
) {
  return useQuery({
    queryKey: queryKeys.sessionTimeline.event(
      organizationId,
      occurrenceId,
      eventId ?? ''
    ),
    queryFn: () =>
      getAlertSessionEvent(
        apiClient,
        organizationId,
        occurrenceId,
        eventId ?? ''
      ),
    enabled: !!eventId,
    staleTime: Infinity,
  });
}

/**
 * Write the new hold state into the cached summary straight away, so every
 * Keep button on this occurrence flips before the refetch lands.
 */
function useSetRetained(organizationId: string | null, occurrenceId: string) {
  const queryClient = useQueryClient();
  return (retained: boolean, expiresAt: string | null) =>
    queryClient.setQueryData<SessionSummary>(
      queryKeys.sessionTimeline.summary(organizationId, occurrenceId),
      old => (old ? { ...old, retained, expiresAt } : old)
    );
}

/**
 * Refetch the summary after Keep or release. The hold state is read from the
 * summary, so the timeline pages and event payloads stay as they are, and the
 * mutation does not wait for the refetch.
 */
function useInvalidateSummary(
  organizationId: string | null,
  occurrenceId: string
) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.sessionTimeline.summary(organizationId, occurrenceId),
    });
  };
}

export function useKeepAlertSession(
  organizationId: string | null,
  occurrenceId: string
) {
  const setRetained = useSetRetained(organizationId, occurrenceId);
  const invalidate = useInvalidateSummary(organizationId, occurrenceId);
  return useMutation({
    mutationFn: () => keepAlertSession(apiClient, organizationId, occurrenceId),
    onSuccess: () => setRetained(true, null),
    onSettled: invalidate,
  });
}

export function useReleaseAlertSession(
  organizationId: string | null,
  occurrenceId: string
) {
  const setRetained = useSetRetained(organizationId, occurrenceId);
  const invalidate = useInvalidateSummary(organizationId, occurrenceId);
  return useMutation({
    mutationFn: () =>
      releaseAlertSession(apiClient, organizationId, occurrenceId),
    onSuccess: data => setRetained(false, data.expiresAt),
    onSettled: invalidate,
  });
}
